import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { dropdownOptions } from '@interfaces/vendor.model';
import { GlobalErrorService } from '@services/global-error.service';
import { VendorService } from '@services/vendor.service';
import { OpsContractorsListView } from '@app/pages/ops-vendor-management/ops-contractors-list/ops-contractors-list.config';
import { ContractorListEnum } from '@shared/constants/global.enum';
import { ActivatedRoute } from '@angular/router';
import { filterContractorsList } from './ops-contractors-list-filter.helper';

@Component({
  selector: 'app-ops-contractors-list-filter',
  templateUrl: './ops-contractors-list-filter.component.html',
  styleUrls: ['./ops-contractors-list-filter.component.scss']
})
export class OpsContractorsListFilterComponent implements OnInit, OnChanges {
  @Input() ContractorDataByFilter: OpsContractorsListView[] = [];
  @Output() filterApplied = new EventEmitter<any>();
  @Output() closeFilter = new EventEmitter<void>();
  @Output() filterReset = new EventEmitter<{ month: number; year: number }>();

  filterForm: FormGroup = new FormGroup({});
  vendorOptions: dropdownOptions[] = [];
  statusOptions: dropdownOptions[] = [];
  regularOptions: dropdownOptions[] = [];
  locationOptions: dropdownOptions[] = [];
  reportingManagerOptions: dropdownOptions[] = [];
  monthOptions: dropdownOptions[] = [];
  rtoOptions: dropdownOptions[] = [];
  opened: boolean = false;
  openForm: boolean = true;
  displayData: any[] = [];
  rowData: any[] = [];
  private isDataLoaded: boolean = false;
  selectedOption: string = '';
  month: number = 0;
  year: number = 0;
  selectedyear: number = 0;
  selectedmonth: number = 0;
  contractorListEnum = ContractorListEnum;

  constructor(
    public fb: FormBuilder,
    public globalError: GlobalErrorService,
    private vendorService: VendorService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      const today = new Date();

      this.selectedmonth = Number(params['month'] || today.getMonth() + 1);
      this.selectedyear = Number(params['year'] || today.getFullYear());

      this.filterForm.patchValue({
        month: `${this.selectedmonth}-${this.selectedyear}`,
      });

      this.initialzeForm();
      this.generateMonthOptions();
      this.loadFormData();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['ContractorDataByFilter'] && changes['ContractorDataByFilter'].currentValue) {
      this.loadFormData();
    }
  }

  initialzeForm() {
    this.filterForm = this.fb.group({
      vendor: [null],
      status: [null],
      regular: [null],
      location: [null],
      reportingManager: [null],
      month: [null],
    });
  }

  generateMonthOptions(): void {
    const today = new Date();
    this.month = today.getMonth() + 1;
    this.year = today.getFullYear();
    this.monthOptions = Array.from({ length: 12 }, (_, i) => {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthName = date.toLocaleString('default', { month: 'long' });
      const year = date.getFullYear();
      return {
        label: `${monthName}, ${year}`,
        value: `${date.getMonth() + 1}-${year}`,
      };
    });

    if (!this.selectedOption && this.monthOptions.length) {
      this.selectedOption = this.monthOptions[0].value;
    }
  }

  apply(): void {
    const getFilterValue = (field: string) => {
      const value = this.filterForm.get(field)?.value;
      return value?.value || value || null;
    };

    const selectedVendor = getFilterValue('vendor');
    const selectedStatus = getFilterValue('status');
    const selectedRegular = getFilterValue('regular');
    const selectedLocation = getFilterValue('location');
    const selectedReportingManager = getFilterValue('reportingManager');
    const selectedMonth = getFilterValue('month');

    if (selectedMonth) {
      const combinedFilters = {
        month: selectedMonth,
        vendor: selectedVendor,
        status: selectedStatus,
        regular: selectedRegular,
        location: selectedLocation,
        reportingManager: selectedReportingManager
      };
      this.filterApplied.emit(combinedFilters);
      this.closeFilter.emit();
      return;
    }

    const filterCriteria = {
      vendor: selectedVendor,
      status: selectedStatus,
      regular: selectedRegular,
      location: selectedLocation,
      reportingManager: selectedReportingManager,
    };

    this.displayData = filterContractorsList(this.ContractorDataByFilter, filterCriteria);
    
    this.filterApplied.emit(this.displayData);
    this.closeFilter.emit();
  }

  onMonthSelect(event: any): void {
    this.selectedOption = event;
    const t = this.setMonthAndYear(this.selectedOption);
    this.selectedyear = Number(t.split('-')[1]);
    this.selectedmonth = Number(t.split('-')[0]);
    
    this.filterForm.patchValue({ month: event });
    this.apply();
  }

  setMonthAndYear(value: string): string {
    return value;
  }

  loadFormData() {
    if (this.ContractorDataByFilter && this.ContractorDataByFilter.length > 0) {
      this.vendorOptions = this.getUniqueValues('vendor');
      this.statusOptions = this.getUniqueValues('status');
      this.locationOptions = this.getUniqueValues('location');
      this.reportingManagerOptions = this.getUniqueValues('reportingManager');
      this.regularOptions = this.getUniqueValues('rto');
      this.isDataLoaded = true;
    }
  }

  private getUniqueValues(key: keyof OpsContractorsListView): dropdownOptions[] {
    if (!this.ContractorDataByFilter || this.ContractorDataByFilter.length === 0) {
      return [];
    }

    const values = this.ContractorDataByFilter
      .map((item) => item[key])
      .filter((value) => value !== '' && value !== null && value !== undefined);
    
    const uniqueValues = [...new Set(values)];
    
    return uniqueValues.map((value) => ({
      label: String(value),
      value: String(value),
    }));
  }

  reset(): void {
    const today = new Date();

    this.month = this.selectedmonth || today.getMonth() + 1;
    this.year = this.selectedyear || today.getFullYear();

    this.selectedOption = `${this.month}-${this.year}`;

    this.filterForm.patchValue({
      vendor: null,
      status: null,
      regular: null,
      location: null,
      reportingManager: null,
      month: this.selectedOption,
    });

    this.opened = true;
    this.openForm = false;
    setTimeout(() => {
      this.openForm = true;
    });

    this.filterReset.emit({
      month: this.month,
      year: this.year,
    });
  }
}

function filterContractors(ContractorDataByFilter: OpsContractorsListView[], filterCriteria: { vendor: any; status: any; regular: any; location: any; reportingManager: any; }): any[] {
  throw new Error('Function not implemented.');
}
